import { useEffect, useState } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import {
  CATEGORY_LABELS,
  ScheduleAssignment,
  ScheduleUser,
  ShiftCategory,
  ShiftTemplate,
  formatDateISO,
} from './utils'

interface Props {
  token: string
  clientId: number
  templates: ShiftTemplate[]
  users: ScheduleUser[]
  assignment?: ScheduleAssignment
  defaultDate?: string
  defaultUserId?: number
  onClose: () => void
  onSaved: () => void
}

export default function AssignmentEditorModal({
  token, clientId, templates, users, assignment, defaultDate, defaultUserId, onClose, onSaved,
}: Props) {
  const { showError, showSuccess } = useNotificationContext()
  const [busy, setBusy] = useState(false)

  const isEdit = !!assignment?.id
  const [date, setDate] = useState<string>(assignment?.date || defaultDate || formatDateISO(new Date()))
  const [userId, setUserId] = useState<number | ''>(assignment?.user_id || defaultUserId || (users[0]?.id ?? ''))
  const [templateId, setTemplateId] = useState<number | ''>(assignment?.shift_template_id || '')
  const [useCustom, setUseCustom] = useState<boolean>(
    !!assignment && (!assignment.shift_template_id || !!assignment.custom_label || !!assignment.custom_start_time)
  )
  const [customLabel, setCustomLabel] = useState<string>(assignment?.custom_label || '')
  const [customStart, setCustomStart] = useState<string>((assignment?.custom_start_time || '').slice(0, 5))
  const [customEnd, setCustomEnd] = useState<string>((assignment?.custom_end_time || '').slice(0, 5))
  const [customColor, setCustomColor] = useState<string>(assignment?.custom_color || '#3b82f6')
  const [customCategory, setCustomCategory] = useState<ShiftCategory>(
    (assignment?.custom_category as ShiftCategory) || 'shift'
  )
  const [note, setNote] = useState<string>(assignment?.note || '')

  useEffect(() => {
    if (!templates.length && !useCustom) setUseCustom(true)
  }, [templates, useCustom])

  const handleSave = async () => {
    if (!userId) { showError('Wybierz osobę'); return }
    if (!date) { showError('Wybierz datę'); return }
    if (!useCustom && !templateId) { showError('Wybierz szablon lub włącz tryb "własne godziny"'); return }

    setBusy(true)
    try {
      const body: any = {
        client_id: clientId,
        user_id: Number(userId),
        date,
        note: note || null,
      }
      if (!useCustom) {
        body.shift_template_id = Number(templateId)
        body.custom_label = null
        body.custom_start_time = null
        body.custom_end_time = null
        body.custom_color = null
        body.custom_category = null
      } else {
        body.shift_template_id = templateId ? Number(templateId) : null
        body.custom_label = customLabel || null
        body.custom_start_time = customStart ? `${customStart}:00` : null
        body.custom_end_time = customEnd ? `${customEnd}:00` : null
        body.custom_color = customColor || null
        body.custom_category = customCategory || null
      }

      const url = isEdit
        ? `/api/schedule/assignments/${assignment!.id}`
        : `/api/schedule/assignments`
      const method = isEdit ? 'PUT' : 'POST'
      const r = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) {
        throw new Error(await r.text())
      }
      showSuccess(isEdit ? 'Wpis zaktualizowany' : 'Wpis dodany')
      onSaved()
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? '✏️ Edycja wpisu grafiku' : '+ Nowy wpis grafiku'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Osoba">
              <select
                className="w-full border-gray-300 rounded-md shadow-sm"
                value={userId}
                onChange={e => setUserId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— wybierz —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.full_name || u.username} {u.role ? `(${u.role})` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Data">
              <input
                type="date"
                className="w-full border-gray-300 rounded-md shadow-sm"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </Field>
          </div>

          <div className="flex items-center gap-3">
            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                className="mr-2"
                checked={!useCustom}
                onChange={() => setUseCustom(false)}
                disabled={!templates.length}
              />
              Użyj szablonu
            </label>
            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                className="mr-2"
                checked={useCustom}
                onChange={() => setUseCustom(true)}
              />
              Własne godziny / etykieta
            </label>
          </div>

          {!useCustom && (
            <Field label="Szablon zmiany">
              <select
                className="w-full border-gray-300 rounded-md shadow-sm"
                value={templateId}
                onChange={e => setTemplateId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— wybierz —</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({(t.start_time || '').slice(0, 5)}–{(t.end_time || '').slice(0, 5)}) – {CATEGORY_LABELS[t.category]}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {useCustom && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-gray-50 rounded border border-gray-200">
              <Field label="Etykieta (np. 'Dyżur', 'Urlop')">
                <input
                  type="text"
                  className="w-full border-gray-300 rounded-md shadow-sm"
                  value={customLabel}
                  onChange={e => setCustomLabel(e.target.value)}
                />
              </Field>
              <Field label="Kategoria">
                <select
                  className="w-full border-gray-300 rounded-md shadow-sm"
                  value={customCategory}
                  onChange={e => setCustomCategory(e.target.value as ShiftCategory)}
                >
                  {(Object.entries(CATEGORY_LABELS) as [ShiftCategory, string][]).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Start">
                <input
                  type="time"
                  className="w-full border-gray-300 rounded-md shadow-sm"
                  value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                />
              </Field>
              <Field label="Koniec">
                <input
                  type="time"
                  className="w-full border-gray-300 rounded-md shadow-sm"
                  value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                />
              </Field>
              <Field label="Kolor" className="sm:col-span-2">
                <input
                  type="color"
                  className="h-9 w-16 border border-gray-300 rounded"
                  value={customColor}
                  onChange={e => setCustomColor(e.target.value)}
                />
              </Field>
            </div>
          )}

          <Field label="Notatka (opcjonalnie)">
            <textarea
              className="w-full border-gray-300 rounded-md shadow-sm"
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="np. zamiana z Janem, dyżur telefoniczny..."
            />
          </Field>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
            onClick={onClose}
            disabled={busy}
          >
            Anuluj
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={handleSave}
            disabled={busy}
          >
            {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children, className = '' }: { label: string, children: React.ReactNode, className?: string }) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="text-gray-700 font-medium">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
